import { useTranslation } from 'react-i18next';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import type { PropsData } from '@packages/global';
import { IProjectAsset } from '@packages/global';
import ItemCard from '../cards/ItemCard';

/**
 * PropsStep 组件属性
 */
export interface PropsStepProps {
  /** 步骤数据 */
  data: PropsData | null;
  /** 脚本 ID */
  scriptId?: string;
  /** 项目 ID */
  projectId?: string;
  /** 集 ID */
  episodeId?: string;
  /** 艺术风格 */
  artStyle?: string;
  /** 是否正在加载 */
  isLoading?: boolean;
  /** 图片正在生成中（步骤 IN_PROGRESS），禁用卡片操作 */
  imageGenerating?: boolean;
  /** 来自 ProjectAsset 的主体列表，用于实时反映生成状态和图片 */
  episodeAssets?: IProjectAsset[];
  /** 重新生成回调 */
  onRegenerate?: () => void;
  /** 数据更新回调 */
  onUpdate?: () => void;
}

/**
 * 空状态组件
 */
function EmptyState({ onRegenerate, t }: { onRegenerate?: () => void; t: (key: string) => string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="w-16 h-16 rounded-full bg-bg-elevated flex items-center justify-center mb-4">
        <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
        </svg>
      </div>
      <h3 className="text-base font-medium text-gray-200 mb-2">{t('script:props.empty')}</h3>
      <p className="text-sm text-gray-400 mb-6 max-w-xs">
        {t('script:props.emptyDesc')}
      </p>
      {onRegenerate && (
        <button
          onClick={onRegenerate}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary hover:bg-primary-dark text-white text-sm font-medium transition-colors cursor-pointer"
        >
          <ArrowPathIcon className="w-4 h-4" />
          {t('script:props.generate')}
        </button>
      )}
    </div>
  );
}

/**
 * PropsStep 组件
 * 
 * 复用原有的 ItemCard 组件，支持：
 * - 图片上传/AI生成
 * - 删除道具
 * 
 * Requirements: 6.6
 */
export default function PropsStep({
  data,
  scriptId,
  projectId,
  episodeId,
  artStyle,
  isLoading = false,
  imageGenerating = false,
  episodeAssets = [],
  onRegenerate,
  onUpdate,
}: PropsStepProps) {
  const { t } = useTranslation(['script', 'common']);
  // 加载状态
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="flex flex-col items-center gap-3">
          <ArrowPathIcon className="w-8 h-8 text-primary animate-spin" />
          <span className="text-sm text-gray-400">{t('script:props.generating')}</span>
        </div>
      </div>
    );
  }

  // 空状态
  if (!data || data.props.length === 0) {
    return <EmptyState onRegenerate={onRegenerate} t={t} />;
  }

  return (
    <div className="space-y-5">
      {/* 道具网格 - 固定宽度 200px，两端对齐 */}
      <div className="flex flex-wrap gap-4">
        {data.props.map((prop, index) => {
          const matchedAsset = episodeAssets.find(a => a.name === prop.name);
          return (
            <div key={index} className="w-[200px] flex-shrink-0">
              <ItemCard
                name={prop.name}
                imageUrl={prop.imageUrl}
                prompt={prop.prompt}
                description={prop.description}
                scriptId={scriptId || ''}
                type="prop"
                projectId={projectId}
                episodeId={episodeId}
                artStyle={artStyle}
                imageGenerating={imageGenerating}
                assetGenerationStatus={matchedAsset?.generationStatus}
                assetImageUrl={matchedAsset?.conceptImageUrl || matchedAsset?.frontViewUrl}
                matchedAsset={matchedAsset}
                onUpdate={onUpdate}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
